import { CHAPTER_LEADERS } from './leaders';
// 敵の名簿。図鑑・式神・ドロップの対象は neutral と boss の100体（e001〜e100）。
// rival は各章の敵ヒーロー（図鑑には別枠で載せる）。能力値は battle が章と役割から決める。

/** prologue＝プロローグ（三百年前・天下統一の最終戦）の人間の軍。図鑑・式神・ドロップの対象外 */
export type EnemyKind = 'neutral' | 'boss' | 'rival' | 'prologue';
export type EnemyRole = 'small' | 'medium' | 'elite' | 'boss' | 'rival';

export interface EnemyDef {
  id: string;
  no: number; // 図鑑番号
  name: string;
  kind: EnemyKind;
  role: EnemyRole;
  chapter: number; // 初登場の章
  /** 敵の型（battle の CFG.enemyTypes）。heavy＝重装（鎧）・siege＝攻城（増援で本陣へ向かう） */
  type?: EnemyType;
}

export type EnemyType = 'heavy' | 'siege';
/** 敵の型の名前と説明（図鑑・情報札） */
export const ENEMY_TYPE_INFO: Record<EnemyType, { name: string; desc: string }> = {
  heavy: { name: '重装', desc: '鎧がある間は物理の攻撃がほとんど通らない。当たるたびに鎧が1枚剥がれる。魔法は鎧を無視する' },
  siege: { name: '攻城', desc: '増援で現れると英雄を相手にせず本陣へ向かい、たどり着くと本陣を大きく削る' },
};

export const CHAPTERS = [
  { no: 1, name: '紅葉の里', terrain: 'meadow' },
  { no: 2, name: '水辺の関', terrain: 'river' },
  { no: 3, name: '竹林の砦', terrain: 'bamboo' },
  { no: 4, name: '古寺の跡', terrain: 'temple' },
  { no: 5, name: '雪の峠', terrain: 'snow' },
  { no: 6, name: '火の山', terrain: 'volcano' },
  { no: 7, name: '霊の野', terrain: 'grave' },
  { no: 8, name: '天狗の山', terrain: 'mountain' },
  { no: 9, name: '鬼ヶ島', terrain: 'oni' },
  { no: 10, name: '百鬼の都', terrain: 'capital' },
] as const;

export const ENEMY_LIST: readonly EnemyDef[] = [
  { id: 'e001', no: 1, name: '豆狸', kind: 'neutral', role: 'small', chapter: 1 },
  { id: 'e002', no: 2, name: '子河童', kind: 'neutral', role: 'small', chapter: 1 },
  { id: 'e003', no: 3, name: 'からかさ小僧', kind: 'neutral', role: 'small', chapter: 1 },
  { id: 'e004', no: 4, name: '提灯お化け', kind: 'neutral', role: 'small', chapter: 1 },
  { id: 'e005', no: 5, name: 'すねこすり', kind: 'neutral', role: 'medium', chapter: 1 },
  { id: 'e006', no: 6, name: '木霊', kind: 'neutral', role: 'medium', chapter: 1 },
  { id: 'e007', no: 7, name: '小鬼', kind: 'neutral', role: 'medium', chapter: 1 },
  { id: 'e008', no: 8, name: '一つ目小僧', kind: 'neutral', role: 'elite', chapter: 1 },
  { id: 'e009', no: 9, name: '化け狐', kind: 'neutral', role: 'elite', chapter: 1 },
  { id: 'e010', no: 10, name: '大たぬきの霊', kind: 'boss', role: 'boss', chapter: 1 },
  { id: 'e011', no: 11, name: '河童', kind: 'neutral', role: 'small', chapter: 2 },
  { id: 'e012', no: 12, name: '濡れ女子', kind: 'neutral', role: 'small', chapter: 2 },
  { id: 'e013', no: 13, name: '磯女', kind: 'neutral', role: 'small', chapter: 2 },
  { id: 'e014', no: 14, name: '川獺', kind: 'neutral', role: 'small', chapter: 2 },
  { id: 'e015', no: 15, name: '小豆洗い', kind: 'neutral', role: 'medium', chapter: 2 },
  { id: 'e016', no: 16, name: '蛟', kind: 'neutral', role: 'medium', chapter: 2 },
  { id: 'e017', no: 17, name: '水虎', kind: 'neutral', role: 'medium', chapter: 2 },
  { id: 'e018', no: 18, name: '赤えい', kind: 'neutral', role: 'elite', chapter: 2 },
  { id: 'e019', no: 19, name: '海坊主', kind: 'neutral', role: 'elite', chapter: 2 },
  { id: 'e020', no: 20, name: '牛鬼', kind: 'boss', role: 'boss', chapter: 2 },
  { id: 'e021', no: 21, name: '鎌鼬', kind: 'neutral', role: 'small', chapter: 3 },
  { id: 'e022', no: 22, name: '野槌', kind: 'neutral', role: 'small', chapter: 3 },
  { id: 'e023', no: 23, name: '管狐', kind: 'neutral', role: 'small', chapter: 3 },
  { id: 'e024', no: 24, name: '化け猫', kind: 'neutral', role: 'small', chapter: 3 },
  { id: 'e025', no: 25, name: '五徳猫', kind: 'neutral', role: 'medium', chapter: 3 },
  { id: 'e026', no: 26, name: '野衾', kind: 'neutral', role: 'medium', chapter: 3 },
  { id: 'e027', no: 27, name: '山彦', kind: 'neutral', role: 'medium', chapter: 3 },
  { id: 'e028', no: 28, name: '送り狼', kind: 'neutral', role: 'elite', chapter: 3 },
  { id: 'e029', no: 29, name: '竹切り狸', kind: 'neutral', role: 'elite', chapter: 3 },
  { id: 'e030', no: 30, name: '九尾の影', kind: 'boss', role: 'boss', chapter: 3 },
  { id: 'e031', no: 31, name: '一反木綿', kind: 'neutral', role: 'small', chapter: 4 },
  { id: 'e032', no: 32, name: 'ぬりかべ', kind: 'neutral', role: 'small', chapter: 4 },
  { id: 'e033', no: 33, name: '琵琶牧々', kind: 'neutral', role: 'small', chapter: 4 },
  { id: 'e034', no: 34, name: '瀬戸大将', kind: 'neutral', role: 'small', chapter: 4 },
  { id: 'e035', no: 35, name: '鳴屋', kind: 'neutral', role: 'medium', chapter: 4 },
  { id: 'e036', no: 36, name: '木魚達磨', kind: 'neutral', role: 'medium', chapter: 4 },
  { id: 'e037', no: 37, name: '白容裔', kind: 'neutral', role: 'medium', chapter: 4 },
  { id: 'e038', no: 38, name: '雲外鏡', kind: 'neutral', role: 'elite', chapter: 4 },
  { id: 'e039', no: 39, name: '骨傘', kind: 'neutral', role: 'elite', chapter: 4 },
  { id: 'e040', no: 40, name: 'がしゃどくろ', kind: 'boss', role: 'boss', chapter: 4 },
  { id: 'e041', no: 41, name: '雪ん子', kind: 'neutral', role: 'small', chapter: 5 },
  { id: 'e042', no: 42, name: '氷柱女', kind: 'neutral', role: 'small', chapter: 5 },
  { id: 'e043', no: 43, name: '雪入道', kind: 'neutral', role: 'small', chapter: 5, type: 'heavy' },
  { id: 'e044', no: 44, name: '山爺', kind: 'neutral', role: 'small', chapter: 5 },
  { id: 'e045', no: 45, name: 'しばれ狐', kind: 'neutral', role: 'medium', chapter: 5 },
  { id: 'e046', no: 46, name: '雪坊', kind: 'neutral', role: 'medium', chapter: 5 },
  { id: 'e047', no: 47, name: '吹雪鬼', kind: 'neutral', role: 'medium', chapter: 5, type: 'heavy' },
  { id: 'e048', no: 48, name: 'つらら童', kind: 'neutral', role: 'elite', chapter: 5 },
  { id: 'e049', no: 49, name: '雪鼬', kind: 'neutral', role: 'elite', chapter: 5 },
  { id: 'e050', no: 50, name: '雪女', kind: 'boss', role: 'boss', chapter: 5 },
  { id: 'e051', no: 51, name: '火車', kind: 'neutral', role: 'small', chapter: 6, type: 'siege' },
  { id: 'e052', no: 52, name: '鬼火', kind: 'neutral', role: 'small', chapter: 6 },
  { id: 'e053', no: 53, name: '不知火', kind: 'neutral', role: 'small', chapter: 6 },
  { id: 'e054', no: 54, name: '釣瓶火', kind: 'neutral', role: 'small', chapter: 6 },
  { id: 'e055', no: 55, name: '火消婆', kind: 'neutral', role: 'medium', chapter: 6 },
  { id: 'e056', no: 56, name: '火吹き達磨', kind: 'neutral', role: 'medium', chapter: 6, type: 'heavy' },
  { id: 'e057', no: 57, name: '赤舌', kind: 'neutral', role: 'medium', chapter: 6 },
  { id: 'e058', no: 58, name: '炎鼠', kind: 'neutral', role: 'elite', chapter: 6 },
  { id: 'e059', no: 59, name: '陰摩羅鬼', kind: 'neutral', role: 'elite', chapter: 6 },
  { id: 'e060', no: 60, name: '輪入道', kind: 'boss', role: 'boss', chapter: 6 },
  { id: 'e061', no: 61, name: '幽霊武者', kind: 'neutral', role: 'small', chapter: 7, type: 'heavy' },
  { id: 'e062', no: 62, name: '狂骨', kind: 'neutral', role: 'small', chapter: 7, type: 'siege' },
  { id: 'e063', no: 63, name: '餓鬼', kind: 'neutral', role: 'small', chapter: 7 },
  { id: 'e064', no: 64, name: '舟幽霊', kind: 'neutral', role: 'small', chapter: 7 },
  { id: 'e065', no: 65, name: '野寺坊', kind: 'neutral', role: 'medium', chapter: 7 },
  { id: 'e066', no: 66, name: '青坊主', kind: 'neutral', role: 'medium', chapter: 7 },
  { id: 'e067', no: 67, name: '経凛々', kind: 'neutral', role: 'medium', chapter: 7 },
  { id: 'e068', no: 68, name: '産女', kind: 'neutral', role: 'elite', chapter: 7 },
  { id: 'e069', no: 69, name: '影女', kind: 'neutral', role: 'elite', chapter: 7 },
  { id: 'e070', no: 70, name: '怨霊将軍', kind: 'boss', role: 'boss', chapter: 7 },
  { id: 'e071', no: 71, name: '木の葉天狗', kind: 'neutral', role: 'small', chapter: 8 },
  { id: 'e072', no: 72, name: '山童', kind: 'neutral', role: 'small', chapter: 8 },
  { id: 'e073', no: 73, name: '覚', kind: 'neutral', role: 'small', chapter: 8 },
  { id: 'e074', no: 74, name: '山姥', kind: 'neutral', role: 'small', chapter: 8 },
  { id: 'e075', no: 75, name: '大蜘蛛', kind: 'neutral', role: 'medium', chapter: 8 },
  { id: 'e076', no: 76, name: '土蜘蛛', kind: 'neutral', role: 'medium', chapter: 8, type: 'siege' },
  { id: 'e077', no: 77, name: '狒々', kind: 'neutral', role: 'medium', chapter: 8, type: 'heavy' },
  { id: 'e078', no: 78, name: '天邪鬼', kind: 'neutral', role: 'elite', chapter: 8 },
  { id: 'e079', no: 79, name: '鴉兵', kind: 'neutral', role: 'elite', chapter: 8 },
  { id: 'e080', no: 80, name: '大天狗', kind: 'boss', role: 'boss', chapter: 8 },
  { id: 'e081', no: 81, name: '青鬼', kind: 'neutral', role: 'small', chapter: 9 },
  { id: 'e082', no: 82, name: '赤鬼', kind: 'neutral', role: 'small', chapter: 9 },
  { id: 'e083', no: 83, name: '黄鬼', kind: 'neutral', role: 'small', chapter: 9 },
  { id: 'e084', no: 84, name: '鬼武者', kind: 'neutral', role: 'small', chapter: 9, type: 'heavy' },
  { id: 'e085', no: 85, name: '牛頭', kind: 'neutral', role: 'medium', chapter: 9, type: 'heavy' },
  { id: 'e086', no: 86, name: '馬頭', kind: 'neutral', role: 'medium', chapter: 9, type: 'siege' },
  { id: 'e087', no: 87, name: '夜叉', kind: 'neutral', role: 'medium', chapter: 9 },
  { id: 'e088', no: 88, name: '鬼女', kind: 'neutral', role: 'elite', chapter: 9 },
  { id: 'e089', no: 89, name: '鬼火番', kind: 'neutral', role: 'elite', chapter: 9 },
  { id: 'e090', no: 90, name: '茨木童子', kind: 'boss', role: 'boss', chapter: 9 },
  { id: 'e091', no: 91, name: '鵺', kind: 'neutral', role: 'small', chapter: 10 },
  { id: 'e092', no: 92, name: '以津真天', kind: 'neutral', role: 'small', chapter: 10 },
  { id: 'e093', no: 93, name: '姑獲鳥', kind: 'neutral', role: 'small', chapter: 10 },
  { id: 'e094', no: 94, name: '百々目鬼', kind: 'neutral', role: 'small', chapter: 10 },
  { id: 'e095', no: 95, name: '大首', kind: 'neutral', role: 'medium', chapter: 10, type: 'siege' },
  { id: 'e096', no: 96, name: '見越し入道', kind: 'neutral', role: 'medium', chapter: 10, type: 'heavy' },
  { id: 'e097', no: 97, name: '二口女', kind: 'neutral', role: 'medium', chapter: 10 },
  { id: 'e098', no: 98, name: '網剪', kind: 'neutral', role: 'elite', chapter: 10 },
  { id: 'e099', no: 99, name: '手の目', kind: 'neutral', role: 'elite', chapter: 10 },
  { id: 'e100', no: 100, name: '酒呑童子', kind: 'boss', role: 'boss', chapter: 10 },
  { id: 'r01', no: 101, name: '化け狸の頭領', kind: 'rival', role: 'rival', chapter: 1 },
  { id: 'r02', no: 102, name: '河童の親分', kind: 'rival', role: 'rival', chapter: 2 },
  { id: 'r03', no: 103, name: '鎌鼬の三兄', kind: 'rival', role: 'rival', chapter: 3 },
  { id: 'r04', no: 104, name: '骸骨武者', kind: 'rival', role: 'rival', chapter: 4 },
  { id: 'r05', no: 105, name: '雪鬼の将', kind: 'rival', role: 'rival', chapter: 5 },
  { id: 'r06', no: 106, name: '火車の頭目', kind: 'rival', role: 'rival', chapter: 6 },
  { id: 'r07', no: 107, name: '亡霊侍', kind: 'rival', role: 'rival', chapter: 7 },
  { id: 'r08', no: 108, name: '烏天狗の長', kind: 'rival', role: 'rival', chapter: 8 },
  { id: 'r09', no: 109, name: '鬼の副将', kind: 'rival', role: 'rival', chapter: 9 },
  // 10章の番人（大将は酒呑童子。battle/stages.ts の CHAPTER_LEADERS）。2026-10-21 に百鬼の軍師から作り替え
  { id: 'r10', no: 110, name: '大嶽丸', kind: 'rival', role: 'rival', chapter: 10 },
  // プロローグ（三百年前・天下統一の最終戦）：百鬼の王に最後まで抗った人間の大名の軍。絵は art/enemies/prologue.webp（5×2）
  { id: 'h00', no: 200, name: '酒呑童子', kind: 'prologue', role: 'rival', chapter: 0 }, // 操作する（三百年前の姿。絵は art/prologue/shuten.webp）
  { id: 'h01', no: 201, name: '足軽', kind: 'prologue', role: 'small', chapter: 0 },
  { id: 'h02', no: 202, name: '弓足軽', kind: 'prologue', role: 'small', chapter: 0 },
  { id: 'h03', no: 203, name: '鉄砲足軽', kind: 'prologue', role: 'small', chapter: 0 },
  { id: 'h04', no: 204, name: '若武者', kind: 'prologue', role: 'medium', chapter: 0 },
  { id: 'h05', no: 205, name: '騎馬武者', kind: 'prologue', role: 'medium', chapter: 0 },
  { id: 'h06', no: 206, name: '僧兵', kind: 'prologue', role: 'medium', chapter: 0 },
  { id: 'h07', no: 207, name: '陰陽師', kind: 'prologue', role: 'elite', chapter: 0 },
  { id: 'h08', no: 208, name: '忍', kind: 'prologue', role: 'elite', chapter: 0 },
  { id: 'h09', no: 209, name: '侍大将', kind: 'prologue', role: 'boss', chapter: 0 },
  { id: 'h10', no: 210, name: '人の世の総大将', kind: 'prologue', role: 'rival', chapter: 0 },
];

export const ENEMIES: Record<string, EnemyDef> = Object.fromEntries(ENEMY_LIST.map((e) => [e.id, e]));
/** 固有装備（u_<id>）の対象。保存済みの装備の defId を変えないため、元のまま（e001〜e100） */
export const DEX_ENEMIES = ENEMY_LIST.filter((e) => e.kind !== 'rival' && e.kind !== 'prologue');
/** 大将と番人を入れ替えた章（data/leaders.ts）：大将になった妖は図鑑から外し、番人を章の最後（章ボスの席）に入れる */
const LEADER_SWAP = new Map(Object.values(CHAPTER_LEADERS).map((l) => [l!.rival, l!.boss]));
/** 図鑑の対象（各章10体・計100体）。大将（敵の操作キャラ）は入れない */
export const DEX_LIST: EnemyDef[] = DEX_ENEMIES.map((e) => (LEADER_SWAP.has(e.id) ? ENEMIES[LEADER_SWAP.get(e.id)!] : e));
const DEX_IDS = new Set(DEX_LIST.map((e) => e.id));
export const isDexEnemy = (id: string) => DEX_IDS.has(id);
/** 図鑑の番号（No.）：番人は入れ替わった章ボスの番号 */
const SEAT_NO = new Map([...LEADER_SWAP].map(([leader, guardian]) => [guardian, ENEMIES[leader].no]));
export const dexNo = (id: string) => SEAT_NO.get(id) ?? ENEMIES[id]?.no ?? 0;
/** 古い記録の置き換え：大将の妖の記録 → その章の番人 */
export const dexMigrateId = (id: string) => LEADER_SWAP.get(id) ?? id;
